import React, {
  ReactElement,
  ReactNode,
  ReactNodeArray,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { toast } from 'react-toastify'
import log from 'loglevel'
import { PERMISSIONS } from 'src/config/roleConfig'
import {
  ResourceWithAuthz,
  useHasRequiredCapabilities,
  useHasRequiredPermissions,
} from 'src/app/AppAuth/AppAuth.hooks'
import { Spinner } from 'src/Throbber/Spinner'
import {
  StandardButton,
  StandardButtonSize,
  StandardButtonVariant,
} from '../Buttons'
import {
  ButtonGroup,
  EditableOnClickContainer,
  ButtonGroupContainer,
} from './Inputs.styles'

export interface BaseEditableOnClickProps {
  initialValue: string
  onSave: (newValue: string) => Promise<void>
  children: ReactNode | ReactNodeArray
  isObjective?: boolean
  requiredPermissions?: PERMISSIONS[]
  requiredCapabilities?: CAPABILITY[]
  resource?: ResourceWithAuthz
  isDisabled?: boolean
}

export interface EditableOnClickProps extends BaseEditableOnClickProps {
  inline?: boolean
  editMode: (props: {
    value?: string
    onChange: (s?: string) => void
    onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  }) => ReactElement
  onClickConfirmationModal?: (onClick: () => void) => void
}

export interface NoButtonsEditableOnClickProps
  extends BaseEditableOnClickProps {
  height?: number
}

/**
 * generally you should use EditOnClick instead
 */
export const EditableOnClick = ({
  inline,
  initialValue,
  editMode,
  onSave,
  children,
  requiredPermissions = [PERMISSIONS.READ, PERMISSIONS.EDIT],
  requiredCapabilities,
  resource,
  onClickConfirmationModal,
  isDisabled,
}: EditableOnClickProps): JSX.Element => {
  const [busy, setBusy] = useState(false)
  const hasPermission_ = useHasRequiredPermissions(requiredPermissions)
  const hasCapabilities_ = useHasRequiredCapabilities(
    requiredCapabilities,
    resource,
  )
  const hasPermissions = requiredCapabilities
    ? hasCapabilities_
    : hasPermission_
  const [isEditing, toggleEditMode] = useReducer((p) => !p, false)
  const [value, setValue] = useState<string | undefined>(initialValue)
  const [isEmpty, setIsEmpty] = useState<boolean>(initialValue === '')

  useEffect(() => {
    setValue(initialValue)
    setIsEmpty(initialValue === '')
  }, [initialValue])

  const handleSave = useCallback(async () => {
    if (!value?.trim()) {
      setValue('')
      setIsEmpty(true)
      onSave('')
    } else {
      try {
        setBusy(true)
        setValue(value.trim())
        setIsEmpty(false)
        await onSave(value.trim())
      } catch (error) {
        log.error('Error saving value', error)
        toast.error('Error saving value')
      } finally {
        setBusy(false)
      }
    }
    toggleEditMode()
  }, [value, onSave])

  return (
    <EditableOnClickContainer
      inline={inline}
      isEditing={isEditing}
      isEmpty={isEmpty}
      onClick={() => {
        if (isEditing || isDisabled) {
          return
        }
        if (onClickConfirmationModal) {
          onClickConfirmationModal(() => toggleEditMode())
        } else {
          toggleEditMode()
        }
      }}
      isDisabled={isDisabled}
    >
      {hasPermissions && isEditing && !isDisabled ? (
        <>
          {editMode({ onChange: setValue, value })}
          <ButtonGroupContainer>
            <ButtonGroup inline={inline}>
              <StandardButton
                variant={StandardButtonVariant.SECONDARY}
                buttonSize={StandardButtonSize.SMALL}
                onClick={() => {
                  setValue(initialValue)
                  toggleEditMode()
                }}
              >
                Cancel
              </StandardButton>
              <StandardButton
                variant={
                  busy
                    ? StandardButtonVariant.SECONDARY
                    : StandardButtonVariant.PRIMARY
                }
                buttonSize={StandardButtonSize.SMALL}
                onClick={handleSave}
              >
                {busy && (
                  <>
                    <Spinner size="s" color="primary" />
                    &nbsp;
                  </>
                )}
                Save
              </StandardButton>
            </ButtonGroup>
          </ButtonGroupContainer>
        </>
      ) : (
        <>{children}</>
      )}
    </EditableOnClickContainer>
  )
}
